import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import Loader from "../../common/Loader";
import DetailsModal from "../../common/DetailsModal";
import {
    getMerchants,
    getMerchantById,
    deleteMerchantById,
} from "../../../services/merchantService";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import CancelPopup from "../../common/CancelPopup";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import SearchBar from "../../common/SearchBar";
import debounce from "lodash.debounce";
import Confirmation from "../../common/OfferList/Confirmation";
import AddIcon from "../../../Assets/Icons/AddIcon.svg";
import PopupContent from "../../Fundraiser/FundraiserReferralListTable/PopupContent";

const InProcessMerchantList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector((state) => state?.merchantReducer?.loading);

    const [allMerchants, setAllMerchants] = useState();
    const [fullMerchantsList, setFullMerchantsList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [count, setCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [merchantDetails, setMerchantDetails] = useState(null);
    const [isMerchantDetailOpen, setIsMerchantDetailOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [merchantToDelete, setMerchantToDelete] = useState(null);

    const items = [
        {
            title: "Merchants",
        },
        {
            title: "In-Process Merchants",
        },
    ];

    const columns = [
        {
            name: "No.",
        },
        {
            name: "Name",
        },
        {
            name: "Date joined",
        },
        {
            name: "Email",
        },
        {
            name: "Contact",
        },
        {
            name: "Address",
        },
        {
            name: "Status",
        },
        {
            name: "Actions",
        },
    ];

    // Function to check if a merchant is in-process (incomplete signup)
    // IMPORTANT: This logic must match the MerchantList component to ensure consistent filtering
    const isInProcess = (merchant) => {
        // Merchants with approved, declined, or pending status are considered complete
        // and should appear in the main merchant list
        const status = merchant?.status?.toLowerCase();
        if (status === "approved" || status === "declined" || status === "pending") {
            return false;
        }

        // Check for completion of signup flow
        // A merchant is considered completed if they have ALL of these key fields:
        // 1. Email (user.email or merchant.email)
        // 2. Contact Number (contactPersonNumber)
        // 3. Address
        // 4. Date Joined (createdAt)
        // NOTE: Using the same field names as MerchantList for consistency

        const hasEmail = merchant?.user?.email || merchant?.email;
        const hasContact = merchant?.contactPersonNumber;
        const hasAddress = merchant?.address;
        const hasDateJoined = merchant?.createdAt;

        // If they have ALL of these, they are considered completed
        // They should appear in the main merchant list (as pending if no status is set)
        if (hasEmail && hasContact && hasAddress && hasDateJoined) {
            return false; // Not in-process, goes to main list
        }

        // Otherwise, they are in-process (missing some required information)
        return true;
    };

    const updatePageSlice = (data, page) => {
        if (!data) return;
        const startIndex = (page - 1) * 50;
        const endIndex = startIndex + 50;
        setAllMerchants(data.slice(startIndex, endIndex));
    };

    const getMerchantsData = async (search) => {
        let searchParams = {
            limit: 1000, // Fetch all for client-side filtering
            status: null,
            duration: null,
        };

        if (search && search.trim()) {
            const searchTerm = search.trim();
            const isContactNumber = /^[\d\s+\-()]+$/.test(searchTerm);
            const isDate = /^[\d/\-]+$/.test(searchTerm) && (searchTerm.includes('/') || searchTerm.includes('-'));

            if (isContactNumber) {
                searchParams.contactSearch = searchTerm;
            } else if (isDate) {
                searchParams.dateSearch = searchTerm;
            } else {
                searchParams.search = searchTerm;
            }
        }

        Object.keys(searchParams).forEach((key) =>
            searchParams[key] === null ? delete searchParams[key] : {}
        );

        const merchantsPromise = await dispatch(getMerchants(searchParams))?.unwrap();
        if (merchantsPromise?.success === true) {
            // Filter to show only in-process merchants
            let inProcessMerchants = merchantsPromise?.data?.filter((merchant) => {
                return isInProcess(merchant); // TRUE means keep in in-process list
            });

            // SORTING: Newest first
            // Use createdAt if available, otherwise fallback to _id timestamp
            inProcessMerchants.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt) : new Date(parseInt(a._id.substring(0, 8), 16) * 1000);
                const dateB = b.createdAt ? new Date(b.createdAt) : new Date(parseInt(b._id.substring(0, 8), 16) * 1000);
                return dateB - dateA; // Descending order
            });

            setFullMerchantsList(inProcessMerchants);

            // Calculate total pages based on filtered list
            const total = Math.ceil((inProcessMerchants?.length || 0) / 50);
            setTotalPages(total || 1);
            setCount(inProcessMerchants?.length || 0);

            // Update current page slice
            // Reset to page 1 if search changed, otherwise keep current page if valid
            const newPage = search ? 1 : currentPage;
            setCurrentPage(newPage);
            updatePageSlice(inProcessMerchants, newPage);
        }
    };

    useEffect(() => {
        getMerchantsData(searchQuery);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (fullMerchantsList.length > 0) {
            updatePageSlice(fullMerchantsList, currentPage);
        }
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top of merchant list when changing pages
        const container = document.getElementById('merchant-list-container');
        if (container) {
            container.scrollIntoView({ behavior: 'smooth' });
        } else {
            // Fallback to window scroll
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                getMerchantsData(query);
            }, 500),
        []
    );

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    const fetchMerchantDetails = async (merchant) => {
        // Set partial details immediately so modal can open
        setMerchantDetails(merchant);
        setIsMerchantDetailOpen(true);

    };

    const openMerchantPopup = (merchant) => {
        fetchMerchantDetails(merchant);
    };

    const closeMerchantPopup = () => {
        setIsMerchantDetailOpen(false);
    };

    const handleDeleteMerchant = (id) => {
        setMerchantToDelete(id);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const deletePromise = await dispatch(
                deleteMerchantById({ id: merchantToDelete })
            )?.unwrap();
            if (deletePromise?.success === true) {
                getMerchantsData(searchQuery);
            }
        } catch (error) {
            console.error("Error deleting merchant:", error);
        } finally {
            setShowDeleteConfirm(false);
            setMerchantToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setMerchantToDelete(null);
    };

    return (
        <>
            {loading === true && <Loader />}
            {isMerchantDetailOpen && (
                <DetailsModal
                    handleClose={closeMerchantPopup}
                    content={<PopupContent merchantDetails={merchantDetails} onDelete={handleDeleteMerchant} />}
                    popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
                />
            )}
            {showDeleteConfirm && (
                <CancelPopup
                    handleNo={handleDeleteCancel}
                    handleYes={handleDeleteConfirm}
                    heading="Delete Merchant"
                    content="Are you sure you want to delete this merchant profile? This action cannot be undone."
                    image={deleteImg}
                    confirmation={false}
                    popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
                />
            )}
            <div className="flex justify-between col-span-12">
                <BreadCrumsHeader heading="In-Process Merchants" items={items} />
                <div className="flex gap-3 items-center">
                    <button
                        onClick={() => navigate("/invitation-list")}
                        className="flex items-center justify-center bg-white text-[#00ADEF] font-[600] text-[14px] h-[38px] px-4 rounded-[5px] transition-colors duration-150 hover:bg-[#F0F9FF] font-nunito border border-[#00ADEF] whitespace-nowrap"
                        style={{
                            boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
                            letterSpacing: 0.1,
                        }}
                    >
                        Invitation List
                    </button>
                    <button
                        onClick={() => navigate("/merchant-list")}
                        className="flex items-center justify-center bg-white text-[#00ADEF] font-[600] text-[14px] h-[38px] px-4 rounded-[5px] transition-colors duration-150 hover:bg-[#F0F9FF] font-nunito border border-[#00ADEF] whitespace-nowrap"
                        style={{
                            boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
                            letterSpacing: 0.1,
                        }}
                    >
                        Merchant List
                    </button>
                    <button
                        onClick={() => navigate("/invitation-form")}
                        className="relative sm:w-[150px] lg:w-[170px] h-[38px] rounded-[5px] bg-[#00ADEF] sm:text-[12px] lg:text-[14px] font-[600] font-nunito text-[#FFFFFF] text-center pl-[1.75rem] pr-[0.5rem] whitespace-nowrap flex items-center justify-center"
                    >
                        <img src={AddIcon} className="absolute left-[0.75rem] top-1/2 transform -translate-y-1/2 w-4 h-4" alt="Add" />
                        <span className="ml-1">Invite New Merchant</span>
                    </button>
                </div>
            </div>
            <div className="w-full flex justify-start items-center mt-4">
                <SearchBar
                    parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
                    divClassName="relative"
                    inputClassName="searchBar w-full p-3 text-[14px] font-poppins font-[400] outline-none"
                    iconClassName="absolute sm:right-[5%] sm:bottom-[25%] lg:right-[3%]"
                    placeholder="Search"
                    value={searchQuery}
                    handleChange={(e) => handleSearchChange(e.target.value)}
                />
            </div>
            <div id="merchant-list-container" className="col-span-12 mt-4">
                <TableContainer style={{ width: "100%", overflowX: "auto" }}>
                    <Table
                        sx={{
                            minWidth: "800px",
                            borderCollapse: "separate",
                            borderSpacing: "0px 8px",
                        }}
                        aria-label="simple table"
                    >
                        <TableHead className="bg-[#00ADEF]">
                            <TableRow
                                className="h-[50px]"
                                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                            >
                                {columns.map((column) => {
                                    return (
                                        <TableCell key={column.name} className="!text-[#FFFFFF]">
                                            {column.name}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {allMerchants?.map((merchant, index) => {
                                return (
                                    <TableRow
                                        key={merchant._id}
                                        className="h-[50px]"
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                            boxShadow: "0px 4px 4px 0px #00000040",
                                            backgroundColor:
                                                (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                                        }}
                                    >
                                        <TableCell className="max-w-[50px]">{(currentPage - 1) * 50 + index + 1}</TableCell>
                                        <TableCell
                                            title={merchant?.businessName}
                                            className="max-w-[120px] truncate"
                                        >
                                            {merchant?.businessName || "N/A"}
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.createdAt}
                                            className="max-w-[120px] truncate"
                                        >
                                            {
                                                merchant?.createdAt
                                                    ? separateDateTimeAndConvertToLocale(merchant?.createdAt)?.localDate
                                                    : merchant?._id
                                                        ? separateDateTimeAndConvertToLocale(new Date(parseInt(merchant._id.substring(0, 8), 16) * 1000).toISOString())?.localDate
                                                        : "N/A"
                                            }
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.user?.email || merchant?.email}
                                            className="max-w-[140px] truncate"
                                        >
                                            {merchant?.user?.email || merchant?.email || "N/A"}
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.contactPersonNumber || merchant?.phoneNumber || merchant?.phone || merchant?.contact || merchant?.mobile}
                                            className="max-w-[120px] truncate"
                                        >
                                            {merchant?.contactPersonNumber || merchant?.phoneNumber || merchant?.phone || merchant?.contact || merchant?.mobile || "N/A"}
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.address || merchant?.location || merchant?.businessAddress || merchant?.business_address}
                                            className="max-w-[180px] truncate"
                                        >
                                            {merchant?.address || merchant?.location || merchant?.businessAddress || merchant?.business_address || "N/A"}
                                        </TableCell>
                                        <TableCell className="max-w-[110px]">
                                            <Confirmation status="in-process" />
                                        </TableCell>
                                        <TableCell className="max-w-[130px]">
                                            <button
                                                className="flex justify-center items-center border border-[#00ADEF] rounded-[6px] p-3 h-[34px]"
                                                onClick={() => openMerchantPopup(merchant)}
                                            >
                                                <span className="font-[600] text-[14px] text-[#00ADEF] font-poppins leading-[22px]">
                                                    View Details
                                                </span>
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Pagination
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />
            </div>
        </>
    );
};

export default InProcessMerchantList;
